import {type IBrand} from '@verdocs/js-sdk';
import {defaultDisclosuresFor} from './BrandDisclosures';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {VerdocsButton, VerdocsPreview} from '@verdocs/web-sdk-react';
import {BrowserTabBar, EmailWindowHeader} from './MockChrome';
import {useTemplates} from '../../react-sdk/queries';

// Inline SVG icons for the faux footer buttons, copied verbatim from verdocs-sign-footer.tsx.
const FOOTER_QUESTION_ICON = `<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10.4809 13.8423H15.4C16.2962 13.8423 17 13.1288 17 12.2764V5.56582C17 4.71348 16.2962 4 15.4 4H4.6C3.70383 4 3 4.71348 3 5.56582V12.2764C3 13.1288 3.70383 13.8423 4.6 13.8423H6.19908L6.2 17L6.20346 16.9997L6.20502 16.9988L10.4809 13.8423ZM6.79895 17.8034C6.35668 18.1298 5.73 18.0406 5.39921 17.6042C5.26989 17.4335 5.2 17.2262 5.2 17.0133L5.19937 14.8423H4.6C3.16406 14.8423 2 13.6935 2 12.2764V5.56582C2 4.14876 3.16406 3 4.6 3H15.4C16.8359 3 18 4.14876 18 5.56582V12.2764C18 13.6935 16.8359 14.8423 15.4 14.8423H10.81L6.79895 17.8034Z" fill="#424242"/></svg>`;
const FOOTER_DECLINE_ICON = `<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M7.14645 7.14645C7.34171 6.95118 7.65829 6.95118 7.85355 7.14645L10 9.29289L12.1464 7.14645C12.3417 6.95118 12.6583 6.95118 12.8536 7.14645C13.0488 7.34171 13.0488 7.65829 12.8536 7.85355L10.7071 10L12.8536 12.1464C13.0488 12.3417 13.0488 12.6583 12.8536 12.8536C12.6583 13.0488 12.3417 13.0488 12.1464 12.8536L10 10.7071L7.85355 12.8536C7.65829 13.0488 7.34171 13.0488 7.14645 12.8536C6.95118 12.6583 6.95118 12.3417 7.14645 12.1464L9.29289 10L7.14645 7.85355C6.95118 7.65829 6.95118 7.34171 7.14645 7.14645ZM3 6C3 4.34315 4.34315 3 6 3H14C15.6569 3 17 4.34315 17 6V14C17 15.6569 15.6569 17 14 17H6C4.34315 17 3 15.6569 3 14V6ZM6 4C4.89543 4 4 4.89543 4 6V14C4 15.1046 4.89543 16 6 16H14C15.1046 16 16 15.1046 16 14V6C16 4.89543 15.1046 4 14 4H6Z" fill="#424242"/></svg>`;
const FOOTER_SAVE_ICON = `<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M3 5C3 3.89543 3.89543 3 5 3H13.3787C13.9091 3 14.4178 3.21071 14.7929 3.58579L16.4142 5.20711C16.7893 5.58218 17 6.09089 17 6.62132V15C17 16.1046 16.1046 17 15 17H5C3.89543 17 3 16.1046 3 15V5ZM5 4C4.44772 4 4 4.44772 4 5V15C4 15.5523 4.44772 16 5 16L5 11.5C5 10.6716 5.67157 10 6.5 10H13.5C14.3284 10 15 10.6716 15 11.5V16C15.5523 16 16 15.5523 16 15V6.62132C16 6.3561 15.8946 6.10175 15.7071 5.91421L14.0858 4.29289C13.8983 4.10536 13.6439 4 13.3787 4L13 4V6.5C13 7.32843 12.3284 8 11.5 8L7.5 8C6.67157 8 6 7.32843 6 6.5L6 4H5ZM7 4L7 6.5C7 6.77614 7.22386 7 7.5 7L11.5 7C11.7761 7 12 6.77614 12 6.5V4L7 4ZM14 16V11.5C14 11.2239 13.7761 11 13.5 11H6.5C6.22386 11 6 11.2239 6 11.5V16H14Z" fill="#424242"/></svg>`;

// Base styling for the faux signing chrome (header + footer), copied from the real
// #verdocs-sign-header (verdocs-sign.scss) and verdocs-sign-footer (verdocs-sign-footer.scss).
// The header is styled old-school via its ID, and brand color is layered on through injected
// style_overrides (see useSigningPreviewStyles) rather than the primary_color field, which is why
// we reproduce the same ID/markup so a brand's overrides land identically. The footer takes no
// brand overrides for now, so it uses our own class and is positioned within the preview box
// rather than fixed to the viewport like the real one. The footer uses a CSS container query 
// (container-type on itself) to drop the powered-by label at 720px and then the links at 540px 
// as its own width shrinks, matching the real component's behavior.
const SIGN_CHROME_CSS = `
#verdocs-sign-header { color: #fff; width: 100%; display: flex; flex: 0 0 46px; font-size: 12px; column-gap: 15px; align-items: center; flex-direction: row; background-color: #33354c; box-shadow: 0 4px 4px 0 rgb(0 0 0 / 24%), 0 0 4px 0 rgb(0 0 0 / 12%); font-family: var(--verdocs-primary-font, inherit); }
#verdocs-sign-header .inner { width: 100%; display: flex; margin: 0 auto; padding: 0 12px; max-width: 1200px; align-items: center; flex-direction: row; column-gap: 10px; }
#verdocs-sign-header .title { font-size: 18px; font-weight: 500; overflow: hidden; white-space: nowrap; padding: 0 10px 0 0; text-overflow: ellipsis; }
.sign-preview-footer { display: flex; flex: 0 0 48px; align-items: center; justify-content: space-between; padding: 0 24px; color: #616161; font-size: 12px; background: #ffffff; border-top: 1px solid #e0e0e0; container-type: inline-size; }
.sign-preview-footer a { color: #616161; cursor: pointer; text-decoration: none; border-bottom: 1px dotted #616161; }
.sign-preview-footer .powered-by { white-space: nowrap; }
.sign-preview-footer .buttons { display: flex; flex-direction: row; align-items: center; gap: 16px; white-space: nowrap; }
.sign-preview-footer .buttons button { display: flex; flex-direction: row; align-items: center; gap: 4px; padding: 0; border: none; outline: none; cursor: pointer; background: transparent; color: #616161; font-size: 12px; font-family: inherit; }
.sign-preview-footer .links { display: flex; flex-direction: row; gap: 8px; white-space: nowrap; }
@container (max-width: 720px) { .sign-preview-footer .powered-by { display: none; } }
@container (max-width: 540px) { .sign-preview-footer .links { display: none; } }
`;

// Mirror the real signing experience, which injects brand.style_overrides into the document head
// (verdocs-sign.tsx applyBrandStyleOverrides). We inject the same CSS, appended after our base so
// brand overrides win, only while the signing tab is active, and remove it on cleanup. Because
// verdocs-sign is shadow:false, the same selectors a brand targets in production apply here too.
function useSigningPreviewStyles(brand: IBrand, active: boolean) {
  const overrides = brand.style_overrides || '';
  useEffect(() => {
    if (!active) return;
    const style = document.createElement('style');
    style.textContent = `${SIGN_CHROME_CSS}\n${overrides}`;
    document.head.appendChild(style);
    return () => style.remove();
  }, [active, overrides]);
}

const PREVIEW_TABS = [
  {id: 'disclosures', label: 'Disclosures'},
  {id: 'signing', label: 'Signing Experience'},
  {id: 'email', label: 'Email Template'},
] as const;

type PreviewTabId = (typeof PREVIEW_TABS)[number]['id'];


function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Pick a readable label color (white or near-black) for a given background, based on perceived
// luminance. Falls back to white for colors we can't parse (e.g. named colors). Used for the email
// CTA button, whose background is the brand's secondary color and may be light or dark.
function readableTextColor(bg: string): string {
  const hex = bg.trim().replace('#', '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) return '#ffffff';
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 150 ? '#1a1a1a' : '#ffffff';
}

export function writeIframe(iframe: HTMLIFrameElement | null, html: string) {
  if (!iframe) return;
  const doc = iframe.contentDocument;
  if (doc) {
    doc.open();
    doc.write(html);
    doc.close();
  }
}

export function buildDisclosurePreviewHtml(brand: IBrand): string {
  const disclosures = brand.disclaimer || defaultDisclosuresFor(brand);
  const styleOverrides = brand.style_overrides || '';

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    /* Mirrors verdocs-disclosure-dialog's own rules: the same variables with the same fallbacks,
       so a brand's overrides land here exactly as they will in the real dialog. */
    body {
      font-family: var(--verdocs-primary-font, Barlow, -apple-system, BlinkMacSystemFont, sans-serif);
      display: flex; align-items: flex-start; justify-content: center;
      min-height: 100vh; padding: 32px 16px;
      background: rgba(0,0,0,0.5);
    }
    .dialog {
      width: 520px; max-width: 95%; background: #fff; border-radius: var(--verdocs-dialog-radius, 8px);
      box-shadow: 0 4px 24px rgba(0,0,0,0.15);
      border-top: 4px solid var(--verdocs-primary-color, #062a30); overflow: hidden;
    }
    .heading {
      display: flex; align-items: center; justify-content: space-between;
      padding: 16px 24px; border-bottom: 1px solid #e5e7eb;
    }
    .title { font-size: 24px; font-weight: var(--verdocs-dialog-title-weight, 600); color: var(--verdocs-dialog-title-color, var(--verdocs-primary-color, #062a30)); line-height: 32px; }
    .close-btn {
      width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
      border-radius: 50%; border: none; background: transparent; cursor: pointer; color: #6b7280;
    }
    .close-btn:hover { background: #f3f4f6; color: #111827; }
    .content { padding: 24px; font-size: 14px; line-height: 20px; color: var(--verdocs-dialog-content-color, #424242); }
    .content ul { list-style: none; padding: 0; margin-bottom: 16px; }
    .content li {
      position: relative; padding-left: 34px; margin-bottom: 12px;
      font-size: 14px; line-height: 20px;
    }
    .content li::before {
      content: ''; position: absolute; left: 0; top: -2px; width: 24px; height: 24px;
      background: #707070;
      mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'/%3E%3C/svg%3E");
      -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'/%3E%3C/svg%3E");
      mask-size: contain; -webkit-mask-size: contain;
    }
    .content a { color: var(--verdocs-link-color, #4c56cb); text-decoration: underline dotted 1px; }
    .accept { display: flex; align-items: flex-start; gap: 12px; margin-top: 4px; }
    .accept input[type=checkbox] { width: 16px; height: 16px; margin-top: 2px; flex-shrink: 0; accent-color: var(--verdocs-checkbox-color, #4c56cb); }
    .accept label { font-size: 14px; line-height: 20px; color: var(--verdocs-dialog-content-color, #424242); cursor: pointer; }
    .footer { padding: 0 24px 24px; display: flex; justify-content: flex-end; gap: 12px; }
    .btn {
      height: 40px; padding: 8px 16px; font-size: 14px; border-radius: var(--verdocs-button-radius, 4px);
      font-family: inherit; cursor: pointer; font-weight: 500;
    }
    .btn-decline {
      background: transparent; border: var(--verdocs-button-secondary-border, 1px solid #d1d5db); color: var(--verdocs-button-secondary-color, #374151); margin-right: auto;
    }
    .btn-decline:hover { background: var(--verdocs-button-secondary-hover-bg, #f9fafb); }
    .btn-proceed {
      background: var(--verdocs-primary-color, #55bc81); border: 1px solid transparent; color: #fff;
    }
    .btn-proceed:hover { background: var(--verdocs-primary-color-hover, #2b995b); }
    .btn-proceed:disabled { background: var(--verdocs-button-disabled-bg, #e5e7eb); color: var(--verdocs-button-disabled-color, #9ca3af); cursor: not-allowed; }
    ${styleOverrides}
  </style>
</head>
<body class="verdocs-custom-theme">
  <div class="dialog">
    <div class="heading">
      <div class="title">e-Signature Disclosures</div>
      <button class="close-btn" title="Close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    </div>
    <div class="content">
      ${disclosures}
      <div class="accept">
        <input type="checkbox" id="accept-cb" />
        <label for="accept-cb">I accept the electronic signature disclosures and agree to proceed with digital signing.</label>
      </div>
    </div>
    <div class="footer">
      <button class="btn btn-decline">Decline</button>
      <button class="btn btn-proceed" disabled>Proceed</button>
    </div>
  </div>
</body>
</html>`;
}

// Exported so the Email Builder renders the same invitation template this tab does.
export function buildEmailPreviewHtml(brand: IBrand): string {
  const name = brand.name || 'Your Organization';
  const logo = brand.full_logo_url || brand.thumbnail_url || 'https://app.verdocs.com/assets/blue-logo.svg';
  const primary = brand.primary_color || '#654dcb';
  const secondary = brand.secondary_color || '#55bc81';
  const ctaTextColor = readableTextColor(secondary);
  // The footer shows the brand's other image: the thumbnail when the header uses the full logo,
  // otherwise the full logo, and the Verdocs mark only when the brand has neither.
  const footerLogo = (brand.full_logo_url ? brand.thumbnail_url : brand.full_logo_url) || 'https://app.verdocs.com/assets/verdocs.png';

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="initial-scale=1.0" />
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
    * { font-family: Inter, sans-serif !important; margin: 0; padding: 0; }
    body { background-color: transparent; }
  </style>
</head>
<body>
<table role="presentation" border="0" cellspacing="0" cellpadding="0" align="center" width="100%">
  <tr>
    <td></td>
    <td width="640">
      <table style="border-collapse: collapse; background-color: #ffffff; max-width: 640px; width: 100%; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">
        <tr>
          <td style="padding: 10px 24px; text-align: center">
            <img style="border: none" width="150" src="${escapeHtml(logo)}" alt="" />
          </td>
        </tr>
        <tr>
          <td style="padding: 0px 24px 30px 24px">
            <table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%" align="center"
                   style="color: #ffffff; background-color: ${escapeHtml(primary)};">
              <tr>
                <td style="padding: 28px 36px 36px 36px; border-radius: 2px; background-color: ${escapeHtml(primary)};
                            color: #ffffff; font-size: 16px; width: 100%; text-align: center;" align="center">
                  <img width="75" height="75" src="https://app.verdocs.com/assets/docicon.png"
                       style="width: 75px; height: 75px" alt="" />
                  <table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%">
                    <tr>
                      <td style="padding-top: 24px; font-size: 16px; border: none; text-align: center; color: #ffffff;" align="center">
                        ${escapeHtml(name)} sent you a document to review and sign.
                      </td>
                    </tr>
                  </table>
                  <table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%">
                    <tr>
                      <td style="padding-top: 24px; font-size: 16px; border: none; text-align: center; color: #ffffff;" align="center">
                        Please review the attached document at your earliest convenience.
                      </td>
                    </tr>
                  </table>
                  <table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%">
                    <tr>
                      <td align="center" style="padding-top: 30px">
                        <div>
                          <table cellspacing="0" cellpadding="0">
                            <tr>
                              <td align="center" height="44"
                                  style="font-size: 15px; color: ${ctaTextColor}; background-color: ${escapeHtml(secondary)};
                                         font-weight: bold; text-align: center; text-decoration: none;
                                         border-radius: 4px; display: block;">
                                <span style="padding: 0px 24px; line-height: 44px; color: ${ctaTextColor}; cursor: pointer;">VIEW DOCUMENT</span>
                              </td>
                            </tr>
                          </table>
                        </div>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding: 0px 24px 24px 24px; color: #000000; font-size: 16px; background-color: white;">
            <table role="presentation" border="0" cellspacing="0" cellpadding="0">
              <tr>
                <td style="padding-bottom: 20px">
                  <div style="font-weight: bold; line-height: 18px; font-size: 15px; color: #333333;">
                    Jane Doe
                  </div>
                </td>
              </tr>
            </table>
            <span style="font-size: 15px; color: #333333; line-height: 20px;">
              Please review and sign.<br />
            </span>
          </td>
        </tr>
        <tr>
          <td style="padding: 30px 24px 45px 24px; background-color: #eaeaea">
            <table border="0" cellspacing="0" cellpadding="0">
              <tbody>
              <tr>
                <td style="font-size: 11px; color: #666666;">
                  <div style="font-size: 11px; color: #666666; padding: 4px 0 0 0;">
                    Powered by&#160;
                  </div>
                </td>
                <td><img src="${escapeHtml(footerLogo)}" height="19" alt="${escapeHtml(name)}" style="border: none" /></td>
              </tr>
              </tbody>
            </table>
            <p style="margin-top: 1em; margin-bottom: 1em; font-size: 13px; color: #666666; line-height: 18px;">
              <b>Do Not Share This Email</b><br />
              This email contains a secure link to your documents. Please do not share this email with others.
            </p>
            <p style="margin-bottom: 1em; font-size: 10px; color: #666666; line-height: 14px;">
              This message was sent to you by ${escapeHtml(name)}. If you would rather not receive email
              from this sender you may contact the sender with your request.
            </p>
          </td>
        </tr>
      </table>
    </td>
    <td></td>
  </tr>
</table>
</body>
</html>`;
}

interface BrandPreviewProps {
  brand: IBrand;
  /** When set, the disclosures tab offers a button that opens the disclosure text editor. */
  onEditDisclosures?: () => void;
}

export const BrandPreview: FC<BrandPreviewProps> = ({brand, onEditDisclosures}) => {
  const [activeTab, setActiveTab] = useState<PreviewTabId>('disclosures');
  useSigningPreviewStyles(brand, activeTab === 'signing');

  const templatesQuery = useTemplates({sort_by: 'name', ascending: true, rows: 100});
  const templates = templatesQuery.data?.templates ?? [];
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  // Default to the first available template once the list loads.
  useEffect(() => {
    if (!selectedTemplateId && templates.length > 0) setSelectedTemplateId(templates[0].id);
  }, [templates, selectedTemplateId]);
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) ?? null;

  const disclosureIframeRef = useRef<HTMLIFrameElement>(null);
  const emailIframeRef = useRef<HTMLIFrameElement>(null);

  const disclosureHtml = useMemo(() => buildDisclosurePreviewHtml(brand), [brand]);
  const emailHtml = useMemo(() => buildEmailPreviewHtml(brand), [brand]);

  useEffect(() => {
    if (activeTab === 'disclosures') writeIframe(disclosureIframeRef.current, disclosureHtml);
  }, [activeTab, disclosureHtml]);

  useEffect(() => {
    if (activeTab === 'email') writeIframe(emailIframeRef.current, emailHtml);
  }, [activeTab, emailHtml]);

  return (
    <div className="pt-4 flex flex-col flex-1 min-h-0">
      <div className="mb-5">
        <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800">
          {PREVIEW_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-[#33364b] shadow-sm dark:bg-gray-700 dark:text-white'
                  : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'disclosures' && (
        <div className="flex flex-col items-center flex-1 min-h-0">
          <p className="text-sm text-gray-500 mb-4 max-w-xl text-center">
            How the e-signing disclosure dialog appears to signers, with your brand's custom disclaimer and any CSS overrides applied.
          </p>
          {onEditDisclosures && (
            <div className="mb-3">
              <VerdocsButton size="small" variant="outline" label={brand.disclaimer ? 'Edit disclosure text' : 'Customize disclosure text'} onClick={onEditDisclosures} />
            </div>
          )}
          <div className="flex-1 w-full max-w-[620px] rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
            <iframe
              ref={disclosureIframeRef}
              title="Disclosure preview"
              className="w-full border-0"
              style={{height: '520px'}}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}

      {activeTab === 'signing' && (
        <div className="flex flex-col flex-1 min-h-0">
          <p className="text-sm text-gray-500 mb-3">
            Live preview of the signing toolbar with your brand's style overrides applied. The document below is a non-interactive preview;
            use the dedicated signing demo to try the full flow.
          </p>
          {templates.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-gray-500 mb-4">
              <span className="whitespace-nowrap">Template</span>
              <select
                value={selectedTemplateId ?? ''}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="min-w-0 max-w-md flex-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100">
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {selectedTemplate ? (
            <div className="flex flex-col flex-1 min-h-[600px] border rounded-lg overflow-hidden bg-[#f6f8fa]">
              <BrowserTabBar title={brand.page_title || brand.name || brand.key} faviconUrl={brand.favicon_url} />
              <div id="verdocs-sign-header">
                <div className="inner">
                  <div className="title">{selectedTemplate.name}</div>
                  <div style={{flex: 1}} />
                  <VerdocsButton size="xsmall" label="Next Required" />
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-auto">
                <VerdocsPreview key={selectedTemplate.id} templateId={selectedTemplate.id} style={{width: '100%', height: '100%'}} />
              </div>
              <div className="sign-preview-footer">
                <div className="powered-by">
                  {brand.powered_by_label &&
                    (brand.powered_by_url ? (
                      <a href={brand.powered_by_url} target="_blank" rel="noopener noreferrer">
                        {brand.powered_by_label}
                      </a>
                    ) : (
                      <span>{brand.powered_by_label}</span>
                    ))}
                </div>
                <div className="buttons">
                  <button type="button">
                    <span dangerouslySetInnerHTML={{__html: FOOTER_QUESTION_ICON}} />
                    Ask Sender a Question
                  </button>
                  <button type="button">
                    <span dangerouslySetInnerHTML={{__html: FOOTER_DECLINE_ICON}} />
                    Decline Signing
                  </button>
                  <button type="button">
                    <span dangerouslySetInnerHTML={{__html: FOOTER_SAVE_ICON}} />
                    Finish Later
                  </button>
                </div>
                <div className="links">
                  {brand.terms_use_url && (
                    <a href={brand.terms_use_url} target="_blank" rel="noopener noreferrer">
                      Terms of Use
                    </a>
                  )}
                  {brand.privacy_policy_url && (
                    <a href={brand.privacy_policy_url} target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                  )}
                </div>
              </div>
            </div>
          ) : templatesQuery.isLoading ? (
            <div className="flex flex-1 min-h-[600px] items-center justify-center rounded-lg border bg-[#f6f8fa] text-sm text-gray-500">
              Loading templates…
            </div>
          ) : (
            <div className="flex flex-1 min-h-[600px] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-[#f6f8fa] p-8 text-center text-sm text-gray-500 dark:border-gray-600">
              Create a template in this account to preview the signing experience.
            </div>
          )}
        </div>
      )}

      {activeTab === 'email' && (
        <div className="flex flex-col items-center flex-1 min-h-0">
          <p className="text-sm text-gray-500 mb-4 max-w-xl text-center">
            Preview of the invitation email recipients receive, showing your brand's logo, colors, and name.
          </p>
          <div className="flex-1 w-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
            <EmailWindowHeader brand={brand} />
            <div className="p-6" style={{backgroundColor: '#eaeaea'}}>
              <iframe
                ref={emailIframeRef}
                title="Email preview"
                className="w-full border-0 bg-transparent"
                style={{height: '680px', maxWidth: '700px', margin: '0 auto', display: 'block'}}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
